import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const fmtInr = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shipTracking, setShipTracking] = useState({});
  const [shippingId, setShippingId] = useState(null);

  const shipOrder = async (orderId) => {
    setShippingId(orderId);
    try {
      const res = await fetch(`${API}/api/admin/orders/${orderId}/ship`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tracking: shipTracking[orderId] || "" }),
      });
      if (res.ok) load();
    } finally {
      setShippingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/orders`, { credentials: "include" });
      if (res.status === 401) {
        setAuthed(false);
        return;
      }
      const data = await res.json();
      setOrders(data.orders || []);
      const sres = await fetch(`${API}/api/admin/stats`, { credentials: "include" });
      if (sres.ok) setStats(await sres.json());
      setAuthed(true);
    } catch {
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(typeof d.detail === "string" ? d.detail : "Login failed");
        return;
      }
      setPassword("");
      load();
    } catch {
      setError("Network error — try again.");
    }
  };

  const logout = async () => {
    await fetch(`${API}/api/admin/logout`, { method: "POST", credentials: "include" });
    setAuthed(false);
    setOrders([]);
  };

  const paid = orders.filter((o) => o.status === "paid");
  const revenue = paid.reduce((s, o) => s + (o.amount || 0), 0);
  const pieces = paid.reduce((s, o) => s + (o.items || []).reduce((q, i) => q + i.qty, 0), 0);
  const toShip = paid.filter((o) => !o.shipped).length;

  return (
    <motion.main
      data-testid="admin-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-black text-paper px-5 sm:px-10 pt-28 sm:pt-32 pb-20"
    >
      <div className="mx-auto max-w-5xl">
        <button
          data-testid="admin-back-button"
          onClick={() => navigate("/")}
          className="relative z-10 font-sans text-paper/60 hover:text-acid text-xs font-bold tracking-[0.25em] transition-colors mb-8"
        >
          ← BACK TO THE SITE
        </button>

        {authed === null || (loading && authed !== false) ? (
          <p className="font-sans text-paper/50 text-sm tracking-[0.3em]" data-testid="admin-loading">LOADING…</p>
        ) : !authed ? (
          <div className="max-w-md">
            <h1 className="font-display text-[clamp(2.4rem,8vw,5rem)] leading-[0.9]" data-testid="admin-gate-title">
              OWNERS <span className="text-pink">ONLY.</span>
            </h1>
            <form onSubmit={login} className="mt-10" data-testid="admin-login-form">
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="OWNER PASSWORD"
                autoComplete="current-password"
                className="w-full bg-transparent border-2 border-paper/30 focus:border-acid outline-none font-sans font-bold tracking-[0.2em] text-paper px-5 py-4 placeholder:text-paper/30"
              />
              {error && (
                <p className="font-sans text-blood text-xs font-bold tracking-[0.2em] mt-3" data-testid="admin-login-error">
                  {error.toUpperCase()}
                </p>
              )}
              <button
                data-testid="admin-login-button"
                type="submit"
                className="mt-5 w-full font-sans bg-pink text-black font-bold tracking-[0.15em] px-6 py-4 hover:bg-acid transition-colors"
              >
                OPEN THE BOOKS →
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-paper/20 pb-6">
              <h1 className="font-display text-[clamp(2.4rem,8vw,5rem)] leading-[0.9]" data-testid="admin-title">
                THE SHUTUP <span className="text-pink">ORDERS.</span>
              </h1>
              <div className="flex gap-3">
                <button
                  data-testid="admin-refresh-button"
                  onClick={load}
                  className="font-sans border-2 border-paper/30 text-paper text-xs font-bold tracking-[0.2em] px-4 py-2.5 hover:border-acid hover:text-acid transition-colors"
                >
                  REFRESH
                </button>
                <button
                  data-testid="admin-logout-button"
                  onClick={logout}
                  className="font-sans border-2 border-paper/30 text-paper/60 text-xs font-bold tracking-[0.2em] px-4 py-2.5 hover:border-blood hover:text-blood transition-colors"
                >
                  LOCK
                </button>
              </div>
            </div>

            {stats && (
              <div className="mt-8 border-2 border-paper/20" data-testid="admin-dropday">
                <div className="px-4 py-3 border-b-2 border-paper/20 flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display text-lg tracking-wide">DROP-DAY <span className="text-turq">REPORT</span></p>
                  <p className="font-sans text-paper/40 text-[10px] tracking-[0.25em]">TODAY (IST) · VISITORS COUNTED WITHOUT COOKIES</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x-2 divide-y-2 lg:divide-y-0 divide-paper/20">
                  {[
                    ["VISITORS", stats.today.visitors, "admin-today-visitors"],
                    ["PAGE VIEWS", stats.today.views, "admin-today-views"],
                    ["ORDERS", stats.today.orders, "admin-today-orders"],
                    ["REVENUE", fmtInr(stats.today.revenue), "admin-today-revenue"],
                    ["PIECES", stats.today.pieces, "admin-today-pieces"],
                    ["JOINED LIST", stats.list_today, "admin-today-list"],
                  ].map(([label, value, tid]) => (
                    <div key={tid} className="px-4 py-4 text-center">
                      <p className="font-display text-xl sm:text-2xl text-paper" data-testid={tid}>{value}</p>
                      <p className="font-sans text-paper/50 text-[10px] tracking-[0.25em] mt-1">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 border-t-2 border-paper/20 divide-y-2 sm:divide-y-0 sm:divide-x-2 divide-paper/20">
                  <div className="px-4 py-4">
                    <p className="font-sans text-paper/50 text-[10px] tracking-[0.25em] mb-3">LAST 7 DAYS — PAGE VIEWS</p>
                    <div className="flex items-end gap-2 h-20" data-testid="admin-week-bars">
                      {stats.week.map((d) => {
                        const max = Math.max(1, ...stats.week.map((x) => x.views));
                        return (
                          <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                            <div
                              className="w-full bg-pink/80"
                              style={{ height: `${Math.max(6, (d.views / max) * 64)}px` }}
                              title={`${d.day}: ${d.views} views / ${d.visitors} visitors`}
                            />
                            <span className="font-sans text-paper/40 text-[9px] tracking-[0.1em]">{d.day}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="px-4 py-4">
                    <p className="font-sans text-paper/50 text-[10px] tracking-[0.25em] mb-3">WHERE THEY CAME FROM (7 DAYS)</p>
                    {stats.top_referrers.length === 0 ? (
                      <p className="font-hand text-xl text-paper/40">no traffic yet — share the link, watch this fill up.</p>
                    ) : (
                      <div className="space-y-1.5" data-testid="admin-refs">
                        {stats.top_referrers.map((r) => (
                          <div key={r.source} className="flex justify-between font-sans text-sm">
                            <span className="text-paper/80">{r.source}</span>
                            <span className="font-bold text-acid">{r.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="font-sans text-paper/30 text-[10px] tracking-[0.2em] mt-3">
                      ALL-TIME PAGE VIEWS: {stats.total_views} · THE LIST: {stats.list_total} REBELS
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-5 border-2 border-paper/20 divide-x-2 divide-y-2 sm:divide-y-0 divide-paper/20 mt-8" data-testid="admin-stats">
              {[
                ["PAID ORDERS", paid.length, "admin-stat-paid"],
                ["TO SHIP", toShip, "admin-stat-toship"],
                ["PENDING", orders.length - paid.length, "admin-stat-pending"],
                ["REVENUE", fmtInr(revenue), "admin-stat-revenue"],
                ["PIECES SOLD", `${pieces} / 200`, "admin-stat-pieces"],
              ].map(([label, value, tid]) => (
                <div key={tid} className="px-4 py-5 text-center">
                  <p className="font-display text-2xl sm:text-3xl text-acid" data-testid={tid}>{value}</p>
                  <p className="font-sans text-paper/50 text-[10px] tracking-[0.25em] mt-1">{label}</p>
                </div>
              ))}
            </div>

            {orders.length === 0 ? (
              <p className="font-hand text-3xl text-paper/50 mt-14 -rotate-1" data-testid="admin-empty">
                no orders yet. they said shut up, nobody bought.
              </p>
            ) : (
              <div className="mt-10 space-y-5" data-testid="admin-orders-list">
                {orders.map((o, i) => (
                  <div
                    key={o.order_id}
                    data-testid={`admin-order-row-${i}`}
                    className="border-2 border-paper/20 p-5 sm:p-6"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span
                          className={`font-sans text-[10px] font-bold tracking-[0.2em] px-3 py-1.5 ${
                            o.status === "paid" ? (o.shipped ? "bg-turq text-black" : "bg-acid text-black") : "bg-pink text-black"
                          }`}
                          data-testid={`admin-order-status-${i}`}
                        >
                          {o.status === "paid" ? (o.shipped ? "SHIPPED ✓" : "PAID") : "PENDING"}
                        </span>
                        <span className="font-sans text-paper/50 text-xs tracking-[0.15em]">{fmtDate(o.created_at)}</span>
                      </div>
                      <span className="font-sans font-bold text-lg" data-testid={`admin-order-amount-${i}`}>
                        {fmtInr(o.amount)}
                      </span>
                    </div>
                    <p className="font-sans font-bold text-sm sm:text-base tracking-[0.1em] mt-4" data-testid={`admin-order-items-${i}`}>
                      {(o.items || []).map((it) => `${it.qty} × ${it.color || "BLACK"} / ${it.size}`).join("  ·  ")}
                    </p>
                    <div className="font-sans text-paper/70 text-sm mt-3 space-y-1" data-testid={`admin-order-customer-${i}`}>
                      <p>{o.customer?.name} — {o.customer?.phone}</p>
                      <p>{o.customer?.email}</p>
                      <p>{o.customer?.address}, {o.customer?.city} — {o.customer?.pin}</p>
                    </div>
                    {o.rebel_line && (
                      <p className="font-hand text-xl text-turq mt-3 -rotate-1">“{o.rebel_line}”</p>
                    )}
                    {o.status === "paid" && !o.shipped && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        <input
                          data-testid={`admin-ship-tracking-${i}`}
                          value={shipTracking[o.order_id] || ""}
                          onChange={(e) => setShipTracking((s) => ({ ...s, [o.order_id]: e.target.value }))}
                          placeholder="COURIER / TRACKING (OPTIONAL)"
                          className="flex-1 min-w-[200px] bg-transparent border-2 border-paper/30 focus:border-turq outline-none font-sans font-bold tracking-[0.15em] text-paper px-3 py-2.5 text-xs placeholder:text-paper/30"
                        />
                        <button
                          data-testid={`admin-ship-button-${i}`}
                          onClick={() => shipOrder(o.order_id)}
                          disabled={shippingId === o.order_id}
                          className="font-sans bg-turq text-black text-xs font-bold tracking-[0.15em] px-4 py-2.5 hover:bg-acid transition-colors disabled:opacity-60"
                        >
                          {shippingId === o.order_id ? "SHIPPING…" : "MARK SHIPPED →"}
                        </button>
                      </div>
                    )}
                    {o.status === "paid" && o.shipped && (
                      <p className="font-sans text-turq/80 text-xs tracking-[0.15em] mt-4" data-testid={`admin-shipped-note-${i}`}>
                        SHIPPED {o.shipped_at ? fmtDate(o.shipped_at).toUpperCase() : ""}{o.tracking ? ` · ${o.tracking}` : ""} — buyer emailed ✓
                      </p>
                    )}
                    <p className="font-sans text-paper/30 text-[10px] tracking-[0.15em] mt-3">
                      ORDER {o.order_id}{o.payment_id ? ` · PAYMENT ${o.payment_id}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.main>
  );
}
